import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import DailySchedule from './components/DailySchedule';
import InterventionDetails from './components/InterventionDetails';
import CompletionModal from './components/CompletionModal';
import { appointmentService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const TechnicianDailyRoutes = () => {
  const navigate = useNavigate();
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const { user } = useAuth();

  // Charger les rendez-vous depuis l'API
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);

        const data = await appointmentService.getAppointmentsBytechnician_id(user?.id);
        
        // DEBUG: Vérifier ce que retourne l'API
        console.log('Données reçues de l\'API:', data);
        console.log('Type de données:', typeof data);
        console.log('Est un tableau?', Array.isArray(data));
        
        // S'assurer que data est un tableau
        let appointmentsData = [];
        
        if (Array.isArray(data)) {
          appointmentsData = data;
        } else if (data && typeof data === 'object') {
          // Si c'est un objet, chercher une propriété qui contient un tableau
          const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
          if (arrayKeys.length > 0) {
            appointmentsData = data[arrayKeys[0]];
          } else if (data.data && Array.isArray(data.data)) {
            appointmentsData = data.data;
          } else if (data.appointments && Array.isArray(data.appointments)) {
            appointmentsData = data.appointments;
          }
        }
        
        console.log('Appointments après formatage:', appointmentsData);
        
        // Formater les données si nécessaire
        const formattedAppointments = formatAppointmentData(appointmentsData);
        setAppointments(formattedAppointments);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des rendez-vous:', err);
        setError('Impossible de charger les rendez-vous. Veuillez réessayer.');
        // En cas d'erreur, utiliser un tableau vide
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);
function calcDuration(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);

  return (eh * 60 + em) - (sh * 60 + sm);
}

  // Fonction pour formater les données de l'API
  const formatAppointmentData = (data) => {
    // S'assurer que data est un tableau
    if (!Array.isArray(data)) {
      console.warn('formatAppointmentData: data n\'est pas un tableau', data);
      return [];
    }
    
    return data.map(appointment => {
      // Vérifier que chaque appointment est un objet
      if (!appointment || typeof appointment !== 'object') {
        console.warn('formatAppointmentData: appointment invalide', appointment);
        return null;
      }
      
      return {
        id: appointment.id || Math.random().toString(36).substr(2, 9),
        customerName: appointment.customer_name ||  'Client inconnu',
        customerAvatar: appointment.customer_avatar || "https://img.rocket.new/generatedImages/rocket_gen_img_default.png",
        customerAvatarAlt: `Photo de ${appointment.customer_name || 'client'}`,
        serviceType: appointment.service_type || appointment.service_name || 'Service non spécifié',
        scheduledTime: appointment.scheduled_time_start || '08:00',
        scheduleddate: appointment.scheduled_date ,
        scheduled_time_start: appointment.scheduled_time_start || '08:00',
        scheduled_time_end: appointment.scheduled_time_end || '09:00',        
        estimatedDuration:calcDuration(appointment.scheduled_time_start || '08:00', appointment.scheduled_time_end || '09:00') + ' mins',
        address: appointment.customer_address || 'Adresse non spécifiée',
        phone: appointment.customer_phone || '+33 0 00 00 00 00',
        urgency: getUrgencyLevel(appointment),
        status: mapStatus(appointment.status),
        notes: appointment.notes || '',
        latitude: appointment.customer_latitude || 48.8566,
        longitude: appointment.customer_longitude || 2.3522,
        // Conserver toutes les propriétés originales
        ...appointment
      };
    }).filter(appointment => appointment !== null); // Filtrer les entrées nulles
  };

  // Fonction pour déterminer le niveau d'urgence
  const getUrgencyLevel = (appointment) => {
    if (!appointment) return 'scheduled';
    
    if (appointment.priority === 'high' || appointment.urgence === 'high') return 'urgent';
    if (appointment.priority === 'medium' || appointment.urgence === 'medium') return 'today';
    return 'scheduled';
  };

  // Fonction pour mapper les statuts
  const mapStatus = (status) => {
    if (!status) return 'planned';
    
    const statusMap = {
      'planned': 'planned',
      'scheduled': 'planned',
      'assigned': 'planned',
      'en_route': 'en_route',
      'on_site': 'in_progress',
      'in_progress': 'in_progress',
      'completed': 'completed',
      'cancelled': 'cancelled'
    };
    return statusMap[status] || 'planned';
  };

  const handleSelectAppointment = (appointment) => {
    if (!appointment) return;
    
    setActiveAppointment(appointment);
    if (appointment.status === 'planned') {
      // Mettre à jour le statut localement
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appointment.id
            ? { ...a, status: 'on_site', startTime: new Date().toISOString() }
            : a
        )
      );
      
      // Mettre à jour le statut dans l'API
      updateAppointmentStatus(appointment.id, 'on_site');
      
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      if (appointmentService.updateAppointmentStatus) {
        await appointmentService.updateAppointmentStatus(appointmentId, status);
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
    }
   
  };

  const handleStatusUpdate = (appointment) => {
    if (!appointment) return;
    
    const statusFlow = {
      planned: 'on_site',
    
    };

    const newStatus = statusFlow[appointment.status];
    if (newStatus) {
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === appointment.id
            ? { 
                ...a, 
                status: newStatus, 
                startTime: newStatus === 'on_site' ? new Date().toISOString() : a.startTime 
              }
            : a
        )
      );
      
      // Mettre à jour le statut dans l'API
      updateAppointmentStatus(appointment.id, newStatus);
      
      if (newStatus === 'on_site') {
        setActiveAppointment({ 
          ...appointment, 
          status: newStatus, 
          startTime: new Date().toISOString() 
        });
      }
    }
    navigate(`/intervention-page/${appointment.id}`);
  };

  const handleNavigate = (appointment) => {
    if (!appointment) return;
    
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${appointment.latitude || 48.8566},${appointment.longitude || 2.3522}`;
    window.open(mapsUrl, '_blank');
  };

  const openInterventionDetails = () => {
    navigate('/intervention-page');
  };

  const handleCall = (appointment) => {
    if (!appointment || !appointment.phone) {
      console.warn('Numéro de téléphone non disponible');
      return;
    }
    window.location.href = `tel:${appointment.phone}`;
  };

  const handleComplete = () => {
    if (activeAppointment) {
      setShowCompletionModal(true);
    }
  };

  const handleConfirmCompletion = async (completionData) => {
    try {
      if (!completionData || !completionData.appointmentId) {
        throw new Error('Données de complétion invalides');
      }

      // Mettre à jour l'API avec les données de complétion
      if (appointmentService.completeAppointment) {
        await appointmentService.completeAppointment(completionData.appointmentId, {
          status: 'completed',
          completed_at: new Date().toISOString(),
          photos: completionData.photos || [],
          signature: completionData.signature || '',
          notes: completionData.notes || ''
        });
      }

      // Mettre à jour l'état local
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === completionData.appointmentId
            ? { 
                ...a, 
                status: 'completed', 
                completedAt: completionData.completedAt || new Date().toISOString(),
                photos: completionData.photos || a.photos,
                signature: completionData.signature || a.signature,
                notes: completionData.notes || a.notes
              }
            : a
        )
      );
      setShowCompletionModal(false);
      setActiveAppointment(null);
    } catch (err) {
      console.error('Erreur lors de la complétion du rendez-vous:', err);
      alert('Erreur lors de l\'enregistrement. Veuillez réessayer.');
    }
  };

  const handleUpdatePhotos = (photos) => {
    setActiveAppointment((prev) => ({
      ...prev,
      photos: photos
    }));
  };

  const handleUpdateSignature = (signature) => {
    setActiveAppointment((prev) => ({
      ...prev,
      signature: signature
    }));
  };

  const handleLogout = () => {
    navigate('/');
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+33123456789';
  };

  const handleQuickBooking = () => {
    navigate('/service-booking-wizard');
  };

  const handleSupportChat = () => {
    console.log('Opening support chat...');
  };

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Chargement des rendez-vous...</p>
        </div>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader
          userRole="technician"
          userName="Marc Lefebvre"
          onLogout={handleLogout}
        />
        <div className="main-content container mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 text-sm font-medium text-red-800 hover:text-red-900"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="technician"
        userName="Marc Lefebvre"
        onLogout={handleLogout}
      />

      <main className="main-content">
        <div className="container mx-auto px-4 py-6">
    <div className="flex items-center justify-center h-[calc(100vh-120px)]">
            <div className="h-full w-full">
              <DailySchedule
                appointments={appointments}
                activeAppointment={activeAppointment}
                onSelectAppointment={handleSelectAppointment}
                open={openInterventionDetails}
                onStatusUpdate={handleStatusUpdate}
                onNavigate={handleNavigate}
                onCall={handleCall}
              />
            </div>

            {/* <div className="h-full">
              {activeAppointment ? (
                <InterventionDetails
                  appointment={activeAppointment}
                  onComplete={handleComplete}
                  onUpdatePhotos={handleUpdatePhotos}
                  onUpdateSignature={handleUpdateSignature}
                />
              ) : (
                <div className="bg-card rounded-lg shadow-sm h-full flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {appointments.length === 0 ? 'Aucun rendez-vous' : 'Sélectionnez une intervention'}
                    </h3>
                    <p className="text-muted-foreground max-w-sm mx-auto">
                      {appointments.length === 0
                        ? 'Aucun rendez-vous n\'est planifié pour aujourd\'hui.'
                        : 'Choisissez une intervention dans votre planning pour commencer le suivi et la documentation'}
                    </p>
                  </div>
                </div>
              )}
            </div> */}
          </div>
        </div>
      </main>

      <QuickActionToolbar
        userRole="technician"
        onEmergencyCall={handleEmergencyCall}
        onQuickBooking={handleQuickBooking}
        onSupportChat={handleSupportChat}
      />

      {showCompletionModal && activeAppointment && (
        <CompletionModal
          appointment={activeAppointment}
          onClose={() => setShowCompletionModal(false)}
          onConfirm={handleConfirmCompletion}
        />
      )}
    </div>
  );
};

export default TechnicianDailyRoutes;